import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getApiBaseUrl, getToken, isTokenExpired, apiUrl } from '../utils/token';
import type { Notification, NotificationContextType } from '../types';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  userId?: number;
  token?: string | null;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, userId, token: propToken }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [toasts, setToasts] = useState<Array<{id: string, title: string, message: string, type: string}>>([]);
  const hasLoadedNotifications = React.useRef(false);

  // Calcola la root dell'API in modo sicuro (aggiunge /api se manca; evita duplicati in dev)
  const getApiRoot = useCallback(() => {
    const base = getApiBaseUrl();
    // base può essere '/api' in dev o 'https://grented.com' in prod
    const trimmed = base.endsWith('/') ? base.slice(0, -1) : base;
    if (trimmed.endsWith('/api')) return trimmed; // già include /api
    return `${trimmed}/api`;
  }, []);

  // Connessione WebSocket
  useEffect(() => {
    if (!userId) return;

    const connectWebSocket = () => {
      const apiRoot = getApiRoot();
      const wsBase = apiRoot.replace(/^http/, 'ws');
      const wsUrl = `${wsBase}/notifications/ws/${userId}`;
      
      const websocket = new WebSocket(wsUrl);

      websocket.onopen = () => {
        // console.log('WebSocket connesso per notifiche');
        setIsConnected(true);
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'notification') {
          const newNotification: Notification = {
            id: data.id,
            title: data.title,
            message: data.message,
            type: data.notification_type,
            data: data.data,
            read: false,
            created_at: data.created_at
          };

          // Evita duplicati: aggiungi solo se non esiste già
          setNotifications(prev => {
            const exists = prev.some(n => n.id === newNotification.id);
            if (exists) {
              // console.log('[NotificationContext] Notifica duplicata ignorata:', newNotification.id);
              return prev;
            }
            return [newNotification, ...prev];
          });
          
          // Mostra toast per nuove notifiche
          showToast(data.title, data.message, data.notification_type);
        } else if (data.type === 'pong') {
          // Risposta al ping
          // console.log('WebSocket pong ricevuto');
        } else if (data.type === 'system') {
          // Notifica di sistema
          showToast(data.title, data.message, data.notification_type);
        }
      };

      websocket.onclose = () => {
        // console.log('WebSocket disconnesso, tentativo riconnessione in 5s');
        setIsConnected(false);
        setTimeout(connectWebSocket, 5000);
      };

      websocket.onerror = (error) => {
        console.error('Errore WebSocket:', error);
        setIsConnected(false);
      };

      setWs(websocket);
    };

    connectWebSocket();

    // Ping periodico per mantenere connessione
    const pingInterval = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (ws) {
        ws.close();
      }
    };
  }, [userId]);

  // Carica notifiche esistenti
  useEffect(() => {
    if (!userId) return;
    if (hasLoadedNotifications.current) {
      // console.log('[NotificationContext] Notifications already loaded, skipping');
      return;
    }

    const loadNotifications = async () => {
      try {
        const token = propToken || getToken();
        if (!token || isTokenExpired(token)) {
          // console.log('Token non disponibile o scaduto, salto caricamento notifiche');
          return;
        }

        // console.log('[NotificationContext] Loading notifications for the first time...');
        const response = await fetch(`${getApiRoot()}/notifications/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Il backend restituisce un oggetto con chiave 'notifications'
          const notificationsArray = data.notifications || [];
          
          // console.log('[NotificationContext] Loaded notifications from backend:', notificationsArray);
          // console.log('[NotificationContext] Unread count from data:', data.unread_count);
          
          // Evita duplicati con notifiche già ricevute via WebSocket
          setNotifications(prev => {
            const existingIds = new Set(prev.map(n => n.id));
            const newNotifications = notificationsArray.filter((n: Notification) => !existingIds.has(n.id));
            
            // console.log('[NotificationContext] Existing notifications:', prev.length);
            // console.log('[NotificationContext] New notifications to add:', newNotifications.length);
            
            // Unisci: prima le esistenti (più recenti via WS), poi quelle dal backend
            const merged = [...prev, ...newNotifications];
            // console.log('[NotificationContext] Total after merge:', merged.length);
            // console.log('[NotificationContext] Unread after merge:', merged.filter(n => !n.read).length);
            
            return merged;
          });
          
          hasLoadedNotifications.current = true;
        } else if (response.status === 401) {
          // console.log('Token non autorizzato per notifiche, potrebbe essere necessario un nuovo login');
        }
      } catch (error) {
        console.error('Errore caricamento notifiche:', error);
      }
    };

    // Carica sempre immediatamente se abbiamo userId
    loadNotifications();
  }, [userId, propToken]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      const token = propToken || getToken();
      if (!token || isTokenExpired(token)) {
        // console.log('Token non disponibile o scaduto, impossibile marcare notifica come letta');
        return;
      }

      const response = await fetch(`${getApiRoot()}/notifications/${id}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        );

        // Invia messaggio via WebSocket
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ 
            action: 'mark_read', 
            notification_id: id 
          }));
        }
      }
    } catch (error) {
      console.error('Errore marcatura notifica:', error);
    }
  }, [ws, propToken]);

  const markAllAsRead = useCallback(async () => {
    try {
      const token = propToken || getToken();
      if (!token || isTokenExpired(token)) {
        // console.log('Token non disponibile o scaduto, impossibile marcare tutte le notifiche come lette');
        return;
      }

      const response = await fetch(`${getApiRoot()}/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => (prev || []).map(n => ({ ...n, read: true })));

        // Invia messaggio via WebSocket
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'mark_all_read' }));
        }
      }
    } catch (error) {
      console.error('Errore marcatura tutte notifiche:', error);
    }
  }, [ws, propToken]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      const token = propToken || getToken();
      if (!token || isTokenExpired(token)) {
        // console.log('Token non disponibile o scaduto, impossibile eliminare notifica');
        return;
      }

      const response = await fetch(`${getApiRoot()}/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }
    } catch (error) {
      console.error('Errore eliminazione notifica:', error);
    }
  }, [propToken]);

  const showToast = useCallback((title: string, message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { id, title, message, type };
    
    setToasts(prev => [...prev, newToast]);

    // Rimuovi toast dopo 5 secondi
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  // Calcola unreadCount in modo reattivo
  const unreadCount = useMemo(() => {
    const count = notifications.filter(n => !n.read).length;
    // console.log('[NotificationContext] unreadCount calculated:', count, 'from', notifications.length, 'notifications');
    // console.log('[NotificationContext] Notifications:', notifications.map(n => ({ id: n.id, read: n.read })));
    return count;
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isConnected,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        showToast,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onClose={(id) => setToasts(prev => prev.filter(t => t.id !== id))} />
    </NotificationContext.Provider>
  );
};

// Componente Toast
interface ToastProps {
  toasts: Array<{id: string, title: string, message: string, type: string}>;
  onClose: (id: string) => void;
}

const ToastContainer: React.FC<ToastProps> = ({ toasts, onClose }) => {
  const getToastColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      case 'error': return '✕';
      default: return 'ℹ';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${getToastColor(toast.type)} text-white p-4 rounded-lg shadow-lg min-w-80 max-w-96 animate-slide-in`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <span className="text-lg">{getIcon(toast.type)}</span>
              <div>
                <h4 className="font-semibold">{toast.title}</h4>
                <p className="text-sm opacity-90">{toast.message}</p>
              </div>
            </div>
            <button
              onClick={() => onClose(toast.id)}
              className="text-white hover:text-gray-200 ml-4"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hook per statistiche notifiche
export const useNotificationStats = () => {
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    by_type: {},
    recent_count: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const token = getToken();
        if (!token || isTokenExpired(token)) {
          // console.log('Token non disponibile o scaduto, salto caricamento statistiche notifiche');
          return;
        }

  const response = await fetch(apiUrl('/notifications/notification-stats'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Errore caricamento statistiche notifiche:', error);
      }
    };

    loadStats();
  }, []);

  return stats;
};
